import React from "react";
import {
  Checkbox,
  Select,
  Label,
  makeStyles,
} from "@fluentui/react-components";
import { CoverageComponentProps } from "./types";

const useStyles = makeStyles({
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  conditionalFields: {
    marginTop: "12px",
    marginLeft: "32px",
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
});

const DamagesCoverage: React.FC<CoverageComponentProps<"damages">> = ({
  config,
  onConfigChange,
  t,
  classes,
}) => {
  const styles = useStyles();

  // Generate insurance limit options (50k to 1M in 100k increments)
  const insuranceLimitOptions = [];
  for (let i = 50000; i <= 1000000; i += 100000) {
    insuranceLimitOptions.push(
      <option key={i} value={i}>
        {i.toLocaleString("cs-CZ")} Kč
      </option>
    );
  }

  return (
    <div className={styles.checkboxGroup}>
      {/* Supplementary Natural Hazard Insurance */}
      <div>
        <Checkbox
          label="Supplementary Natural Hazard Insurance"
          checked={config.naturalHazard || false}
          onChange={(_, data) => {
            onConfigChange({
              naturalHazard: data.checked === true,
              // Reset limit when unchecked
              ...(data.checked === false ? { naturalHazardLimit: 50000 } : {}),
            });
          }}
        />
        {config.naturalHazard && (
          <div className={styles.conditionalFields}>
            <div className={styles.fieldGroup} style={{ width: "200px" }}>
              <Label htmlFor="naturalHazardLimit" required>
                Insurance limit
              </Label>
              <Select
                id="naturalHazardLimit"
                value={(config.naturalHazardLimit || 50000).toString()}
                onChange={(_, data) =>
                  onConfigChange({
                    naturalHazardLimit: parseInt(data.value),
                  })
                }
              >
                {insuranceLimitOptions}
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Collision with Animal */}
      <Checkbox
        label="Collision with Animal (including Parked Vehicle Damage)"
        checked={config.collisionWithAnimal || false}
        onChange={(_, data) => {
          onConfigChange({
            collisionWithAnimal: data.checked === true,
          });
        }}
      />

      {/* Pothole Damage Insurance */}
      <Checkbox
        label="Pothole Damage Insurance"
        checked={config.potholeDamage || false}
        onChange={(_, data) => {
          onConfigChange({
            potholeDamage: data.checked === true,
          });
        }}
      />

      {/* Replacement Vehicle Rental Insurance */}
      <div>
        <Checkbox
          label="Replacement Vehicle Rental Insurance"
          checked={config.replacementVehicle || false}
          onChange={(_, data) => {
            onConfigChange({
              replacementVehicle: data.checked === true,
              // Reset values when unchecked
              ...(data.checked === false
                ? {
                    replacementVehicleDailyLimit: 900,
                    replacementVehicleDays: 5,
                  }
                : {}),
            });
          }}
        />
        {config.replacementVehicle && (
          <div className={styles.conditionalFields}>
            <div className={styles.fieldGroup} style={{ width: "150px" }}>
              <Label htmlFor="dailyLimit" required>
                Daily limit
              </Label>
              <Select
                id="dailyLimit"
                value={(config.replacementVehicleDailyLimit || 900).toString()}
                onChange={(_, data) =>
                  onConfigChange({
                    replacementVehicleDailyLimit: parseInt(data.value),
                  })
                }
              >
                <option value="900">900 Kč</option>
                <option value="1500">1,500 Kč</option>
                <option value="2000">2,000 Kč</option>
                <option value="3500">3,500 Kč</option>
                <option value="5000">5,000 Kč</option>
              </Select>
            </div>
            <div className={styles.fieldGroup} style={{ width: "150px" }}>
              <Label htmlFor="numberOfDays" required>
                Number of days
              </Label>
              <Select
                id="numberOfDays"
                value={(config.replacementVehicleDays || 5).toString()}
                onChange={(_, data) =>
                  onConfigChange({
                    replacementVehicleDays: parseInt(data.value),
                  })
                }
              >
                <option value="5">5 days</option>
                <option value="10">10 days</option>
                <option value="15">15 days</option>
                <option value="20">20 days</option>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DamagesCoverage;
